import { useState, useReducer, useMemo, useCallback, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ErrorManagement, LoadingState } from '../LoadingStateUtil';
import {
  CreateGridLayout,
  UpdateGridLayout,
  CreateGridFilter,
  UpdateGridFilter,
  DeleteGridLayout,
  DeleteGridFilter,
  GetGridViews,
  GetGridComponents,
  GridComponent,
  GridLayout,
  GridFilter,
  ComponentGridViewModel,
  ensureFilterItemsHaveIds,
  CreateGridComponent,
  GridViewState,
} from '../../data/GridViewData';
import { GridViewFormInput, defaultFormValues } from '../grid/gridView/GridViewForm';
import useAuth from '../../auth/UseAuth';

export interface FilterState {
  scope: 'researchProject' | 'currentStep' | 'stepType' | 'none';
}

export interface ModalState {
  showLayoutForm: boolean;
  showFilterForm: boolean;
  selectedLayout: GridLayout | null;
  selectedFilter: GridFilter | null;
}

export interface UseGridViewModalStateProps {
  componentName: string;
  transitionEnumId?: string;
  configuredTransitionId?: string;
  researchProjectId?: string;
  onClose?: () => void;
  onApplyView?: (viewModel: ComponentGridViewModel) => void;
  getCurrentGridState: () => GridViewState;
  currentViewModel: ComponentGridViewModel;
}

const INITIAL_FILTER_STATE: FilterState = {
  scope: 'researchProject',
};

const INITIAL_MODAL_STATE: ModalState = {
  showLayoutForm: false,
  showFilterForm: false,
  selectedLayout: null,
  selectedFilter: null,
};

type FilterAction = { type: 'SET_SCOPE'; scope: FilterState['scope'] } | { type: 'RESET_FILTERS' };

type ModalAction =
  | { type: 'TOGGLE_LAYOUT_FORM' }
  | { type: 'TOGGLE_FILTER_FORM' }
  | { type: 'SELECT_LAYOUT'; layout: GridLayout | null }
  | { type: 'SELECT_FILTER'; filter: GridFilter | null }
  | { type: 'RESET' };

function filterReducer(state: FilterState, action: FilterAction): FilterState {
  switch (action.type) {
    case 'SET_SCOPE':
      return { ...state, scope: action.scope };
    case 'RESET_FILTERS':
      return { ...INITIAL_FILTER_STATE };
    default:
      return state;
  }
}

function modalReducer(state: ModalState, action: ModalAction): ModalState {
  switch (action.type) {
    case 'TOGGLE_LAYOUT_FORM':
      return { ...state, showLayoutForm: !state.showLayoutForm, showFilterForm: false };
    case 'TOGGLE_FILTER_FORM':
      return { ...state, showFilterForm: !state.showFilterForm, showLayoutForm: false };
    case 'SELECT_LAYOUT':
      return { ...state, selectedLayout: action.layout, showLayoutForm: false };
    case 'SELECT_FILTER':
      return { ...state, selectedFilter: action.filter, showFilterForm: false };
    case 'RESET':
      return { ...INITIAL_MODAL_STATE };
    default:
      return state;
  }
}

const findComponentByFilters = (
  components: GridComponent[],
  filterState: FilterState,
  configuredTransitionId?: string,
  transitionEnumId?: string,
  researchProjectId?: string
): GridComponent | null => {
  const scope = filterState.scope;

  return (
    components.find(
      c =>
        (scope === 'researchProject' && c.researchProjectId === researchProjectId) ||
        (scope === 'currentStep' && c.configuredTransitionId === configuredTransitionId) ||
        (scope === 'stepType' && c.transitionEnumId === transitionEnumId) ||
        (scope === 'none' && !c.researchProjectId && !c.configuredTransitionId && !c.transitionEnumId)
    ) || null
  );
};

export function useGridViewModalState({
  componentName,
  transitionEnumId,
  configuredTransitionId,
  researchProjectId,
  onClose,
  onApplyView,
  getCurrentGridState,
  currentViewModel,
}: UseGridViewModalStateProps) {
  const { accessToken } = useAuth();

  const [filterState, dispatchFilter] = useReducer(filterReducer, INITIAL_FILTER_STATE);
  const [modalState, dispatchModal] = useReducer(modalReducer, INITIAL_MODAL_STATE);

  const [isOpen, setIsOpen] = useState(false);
  const [gridComponents, setGridComponents] = useState<GridComponent[]>([]);
  const [gridLayouts, setGridLayouts] = useState<GridLayout[]>([]);
  const [gridFilters, setGridFilters] = useState<GridFilter[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>({ status: 'NotStarted' });

  const hasFoundComponentById = useRef(false);

  const layoutForm = useForm<GridViewFormInput>({ defaultValues: defaultFormValues, mode: 'onChange' });
  const filterForm = useForm<GridViewFormInput>({ defaultValues: defaultFormValues, mode: 'onChange' });

  // First try to find by currentGridComponentId
  // If not found, use the filters to find a matching component
  const gridComponent = useMemo<GridComponent | null>(() => {
    if (!hasFoundComponentById.current && currentViewModel?.currentGridComponentId) {
      const foundById = gridComponents.find(c => c.gridComponentId === currentViewModel.currentGridComponentId);
      if (foundById) {
        hasFoundComponentById.current = true;
        return foundById;
      }
    }

    return findComponentByFilters(
      gridComponents,
      filterState,
      configuredTransitionId,
      transitionEnumId,
      researchProjectId
    );
  }, [
    gridComponents,
    filterState,
    configuredTransitionId,
    transitionEnumId,
    researchProjectId,
    currentViewModel?.currentGridComponentId,
  ]);

  const selectLayoutFromViewModel = useMemo(
    () => gridLayouts.find(l => l.gridLayoutId === currentViewModel?.currentGridLayoutId),
    [currentViewModel?.currentGridLayoutId, gridLayouts]
  );

  const selectFilterFromViewModel = useMemo(
    () => gridFilters.find(f => f.gridFilterId === currentViewModel?.currentGridFilterId),
    [currentViewModel?.currentGridFilterId, gridFilters]
  );

  const canApplyView = useMemo(
    () => !!(modalState.selectedLayout || modalState.selectedFilter),
    [modalState.selectedLayout, modalState.selectedFilter]
  );

  const fetchAllData = useCallback(async () => {
    if (!accessToken || !isOpen) return;

    ErrorManagement('Loading', setLoadingState, async () => {
      const components = await GetGridComponents(accessToken, componentName);
      setGridComponents(components || []);

      const matchingComponent = findComponentByFilters(
        components || [],
        filterState,
        configuredTransitionId,
        transitionEnumId,
        researchProjectId
      );

      if (!matchingComponent) {
        try {
          const newComponent = await CreateGridComponent(accessToken, {
            componentName,
            transitionEnumId: filterState.scope === 'stepType' ? transitionEnumId : undefined,
            configuredTransitionId: filterState.scope === 'currentStep' ? configuredTransitionId : undefined,
            researchProjectId: filterState.scope === 'researchProject' ? researchProjectId : undefined,
          });
          setGridComponents(prev => [...prev, newComponent]);
          setGridLayouts([]);
          setGridFilters([]);
          dispatchModal({ type: 'RESET' });
        } catch (error) {
          console.error('Failed to create grid component:', error);
        }
      } else if (matchingComponent?.gridComponentId) {
        const gridView = await GetGridViews(accessToken, matchingComponent.gridComponentId);
        setGridLayouts(gridView.gridLayouts);
        setGridFilters(gridView.gridFilters);
      }

      setLoadingState({ status: 'NotStarted' });
    });
  }, [accessToken, componentName, isOpen, filterState, configuredTransitionId, transitionEnumId, researchProjectId]);

  const clearData = useCallback(() => {
    setGridComponents([]);
    setGridLayouts([]);
    setGridFilters([]);
    setLoadingState({ status: 'NotStarted' });
    hasFoundComponentById.current = false;
  }, []);

  const handleLayoutSuccess = (layout: GridLayout) => {
    dispatchModal({ type: 'SELECT_LAYOUT', layout });
    setLoadingState({ status: 'NotStarted' });
  };

  const handleFilterSuccess = (filter: GridFilter) => {
    dispatchModal({ type: 'SELECT_FILTER', filter });
    setLoadingState({ status: 'NotStarted' });
  };

  useEffect(() => {
    if (isOpen) fetchAllData();
  }, [isOpen, fetchAllData]);

  useEffect(() => {
    if (isOpen && selectLayoutFromViewModel) {
      dispatchModal({ type: 'SELECT_LAYOUT', layout: selectLayoutFromViewModel });
    }
  }, [isOpen, selectLayoutFromViewModel]);

  useEffect(() => {
    if (isOpen && selectFilterFromViewModel) {
      dispatchModal({ type: 'SELECT_FILTER', filter: selectFilterFromViewModel });
    }
  }, [isOpen, selectFilterFromViewModel]);

  const closeModal = () => {
    setIsOpen(false);
    dispatchModal({ type: 'RESET' });
    onClose?.();
    clearData();
  };

  const handleApplyView = () => {
    if (onApplyView && (modalState.selectedLayout || modalState.selectedFilter)) {
      const currentGridState = getCurrentGridState();
      const processedFilterOptions = modalState.selectedFilter?.filterOptions
        ? ensureFilterItemsHaveIds(modalState.selectedFilter.filterOptions)
        : ensureFilterItemsHaveIds(currentGridState.filterOptions);

      const viewModel: ComponentGridViewModel = {
        currentGridComponentId: gridComponent?.gridComponentId,
        currentGridLayoutId: modalState.selectedLayout?.gridLayoutId,
        currentGridFilterId: modalState.selectedFilter?.gridFilterId,
        sortOptions: modalState.selectedLayout?.sortOptions || currentGridState.sortOptions,
        columnVisibility: modalState.selectedLayout?.columnVisibility || currentGridState.columnVisibility,
        columnOrder: modalState.selectedLayout?.columnOrder || currentGridState.columnOrder,
        filterOptions: processedFilterOptions,
      };
      onApplyView(viewModel);
    }
    closeModal();
  };

  const saveLayout = async (data: GridViewFormInput) => {
    if (!accessToken || !gridComponent?.gridComponentId) return;

    ErrorManagement('Loading', setLoadingState, async () => {
      const currentGridState = getCurrentGridState();
      const newGridLayout: GridLayout = {
        name: data.name,
        sortOptions: currentGridState.sortOptions,
        columnVisibility: currentGridState.columnVisibility,
        columnOrder: currentGridState.columnOrder,
        isPublic: data.isPublic,
        publicPermission: data.isPublic ? data.publicPermission : undefined,
      };

      const newLayout = await CreateGridLayout(accessToken, gridComponent.gridComponentId!, newGridLayout);
      handleLayoutSuccess(newLayout);
    });
  };

  const updateLayout = async (data: GridViewFormInput) => {
    if (!accessToken || !modalState.selectedLayout?.gridLayoutId) return;

    ErrorManagement('Loading', setLoadingState, async () => {
      const currentGridState = getCurrentGridState();
      const updatedLayout: GridLayout = {
        ...modalState.selectedLayout!,
        name: data.name,
        sortOptions: data.updateWithCurrent ? currentGridState.sortOptions : modalState.selectedLayout!.sortOptions,
        columnVisibility: data.updateWithCurrent
          ? currentGridState.columnVisibility
          : modalState.selectedLayout!.columnVisibility,
        columnOrder: data.updateWithCurrent ? currentGridState.columnOrder : modalState.selectedLayout!.columnOrder,
        isPublic: data.isPublic,
        publicPermission: data.isPublic ? data.publicPermission : undefined,
      };

      const newLayout = await UpdateGridLayout(accessToken, modalState.selectedLayout!.gridLayoutId!, updatedLayout);
      handleLayoutSuccess(newLayout);
    });
  };

  const deleteLayout = async () => {
    if (!accessToken || !modalState.selectedLayout?.gridLayoutId) return;

    ErrorManagement('Loading', setLoadingState, async () => {
      await DeleteGridLayout(accessToken, modalState.selectedLayout!.gridLayoutId!);
      await fetchAllData();
      dispatchModal({ type: 'SELECT_LAYOUT', layout: null });
    });
  };

  const saveFilter = async (data: GridViewFormInput) => {
    if (!accessToken || !gridComponent?.gridComponentId) return;

    ErrorManagement('Loading', setLoadingState, async () => {
      const currentGridState = getCurrentGridState();
      const processedFilterModel = ensureFilterItemsHaveIds(currentGridState.filterOptions);
      const newGridFilter: GridFilter = {
        name: data.name,
        filterOptions: { items: processedFilterModel.items, logicOperator: processedFilterModel.logicOperator },
        isPublic: data.isPublic,
        publicPermission: undefined,
      };

      const newFilter = await CreateGridFilter(accessToken, gridComponent.gridComponentId!, newGridFilter);
      handleFilterSuccess(newFilter);
    });
  };

  const updateFilter = async (data: GridViewFormInput) => {
    if (!accessToken || !modalState.selectedFilter?.gridFilterId) return;

    ErrorManagement('Loading', setLoadingState, async () => {
      const currentGridState = getCurrentGridState();
      const processedFilterModel = ensureFilterItemsHaveIds(currentGridState.filterOptions);
      const updatedFilter: GridFilter = {
        ...modalState.selectedFilter!,
        name: data.name,
        filterOptions: data.updateWithCurrent
          ? { items: processedFilterModel.items, logicOperator: processedFilterModel.logicOperator }
          : modalState.selectedFilter!.filterOptions,
        isPublic: data.isPublic,
        publicPermission: data.isPublic ? data.publicPermission : undefined,
      };

      const newFilter = await UpdateGridFilter(accessToken, modalState.selectedFilter!.gridFilterId!, updatedFilter);
      handleFilterSuccess(newFilter);
    });
  };

  const deleteFilter = async () => {
    if (!accessToken || !modalState.selectedFilter?.gridFilterId) return;

    ErrorManagement('Loading', setLoadingState, async () => {
      await DeleteGridFilter(accessToken, modalState.selectedFilter!.gridFilterId!);
      await fetchAllData();
      dispatchModal({ type: 'SELECT_FILTER', filter: null });
    });
  };

  return {
    isOpen,
    filterState,
    modalState,
    gridComponent,
    gridLayouts,
    gridFilters,
    loadingState,
    canApplyView,
    dispatchFilter,
    dispatchModal,
    setIsOpen,
    closeModal,
    handleApplyView,
    saveLayout,
    updateLayout,
    deleteLayout,
    saveFilter,
    updateFilter,
    deleteFilter,
    layoutForm,
    filterForm,
  };
}
